<template>
  <ul :class="className">
    <li
      :class="stepClassName(step.modifiers, index)"
      v-for="(step, index) in steps"
      :key="index">
      <div class="timeline__axis">
        <div class="timeline__bullet" />
        <div class="timeline__line" />
      </div>
      <div
        class="timeline__label"
        v-html="step.label" />
      <p
        class="timeline__text"
        v-html="step.text" />
    </li>
    <li
      v-if="showFinishCopy"
      class="timeline__finish"
      v-html="finishCopy" />
  </ul>
</template>

<script>
import bemBlockModifiers from "@/utils/bem-block-modifiers";

export default {
  name: "Timeline",
  props: {
    steps: { type: Array, default: () => ([]) },
    showFinishCopy: { type: Boolean, default: true },
    finishCopy: { type: String, default: null },
    modifiers: { type: Array, default: () => ([]) }
  },
  computed: {
    className() {
      return bemBlockModifiers("timeline", this.modifiers);
    }
  },
  methods: {
    stepClassName(modifiers = [], index) {
      const mods = [...modifiers];
      const elementName = "timeline__step";
      if (index >= this.steps.length - 1) {
        mods.push("last");

        if (!this.showFinishCopy) {
          mods.push("no-finish-copy");
        }

        if (this.finishCopy) {
          mods.push("before-finish");
        }
      }
      return [
        elementName,
        ...mods.map(m => `${elementName}--${m}`)
      ];
    }
  }
};
</script>

<style lang="scss">
.timeline {
  $root: &;
  $margin-bottom-date: $spacing-24;
  $margin-top-bullet: $spacing-4;
  $size-bullet: $spacing-16;

  @include font-family(sans-serif);

  display: flex;
  flex-direction: column;
  padding-left: $spacing-16;

  &--no-padding {
    padding-left: 0;
  }

  &--long-line-xs {
    @include media(max-width, m) {
      #{$root}__step {

        &:first-child {
          margin-bottom: $spacing-48;
        }
      }

      #{$root}__line {
        bottom: -$spacing-64;
      }
    }
  }

  &--bookListPage {
    #{$root}__step:first-child {
      margin-bottom: $spacing-48;
    }

    #{$root}__line {
      bottom: -$spacing-64;
    }
  }

  &__step {
    position: relative;

    &--last {
      margin: 0;
      padding-bottom: 1.5rem; /* make last arrow longer */

      #{$root} {

        &__line {
          bottom: 0;

          width: 2px;

          background-color: $blue5;

          // Arrow branches
          /* stylelint-disable unit-blacklist */
          &:before,
          &:after {
            content: "";

            position: absolute;

            background-color: $blue5;

            border-radius: spacing(2px);

            @include media(max-width, m) {
              right: -1px;
              bottom: 1px;

              width: spacing(11px);
              height: spacing(2px);

              transform: rotate(45deg);
            }

            @include media(min-width, m) {
              right: 1px;
              bottom: -1px;

              width: spacing(2px);
              height: spacing(11px);

              transform: rotate(-45deg);
            }
          }

          &:after {
            @include media(max-width, m) {
              right: auto;
              left: -1px;

              transform: rotate(-45deg);
            }

            @include media(min-width, m) {
              top: -1px;
              bottom: auto;

              transform: rotate(-135deg);
            }
          }
          /* stylelint-enable */

          @include media(min-width, m) {
            width: 100%;
            height: 2px;
          }
        }
      }

      @include media(min-width, m) {
        flex-grow: 1;

        margin-bottom: 0;
        padding-right: $spacing-16;
        padding-bottom: 0;
      }
    }

    &--reminder {

      #{$root}__bullet {
        background-image: url("~@images/shared/bell.svg");
        background-repeat: no-repeat;
        background-position: center;
        background-size: auto;
      }
    }

    &--before-finish {

      #{$root}__text {

        @include media(max-width, m) {
          margin-bottom: $spacing-16;
        }
      }
    }

    &--no-finish-copy {
      padding-bottom: 0; /* remove extra padding from the arrow, since there's no copy */

      #{$root}__text {
        @include media(max-width, m) {
          margin-bottom: 0;
        }
      }
    }

    @include media(max-width, m) {
      display: grid;
      grid-template-columns: $size-bullet + $spacing-16 auto;

      &:not(&--last) {
        margin-bottom: $margin-bottom-date;
      }
    }

    @include media(min-width, m) {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
    }
  }

  &__axis {
    order: 1;
    margin-bottom: $spacing-4;

    @include media(min-width, m) {
      position: relative;

      order: 2;
    }
  }

  &__bullet {
    position: relative;
    z-index: 2;

    display: block;
    flex-shrink: 0;
    width: $size-bullet;
    height: $size-bullet;
    margin-top: $margin-top-bullet;
    margin-right: $spacing-16;

    background-color: $blue;

    border-radius: 100%;
  }

  &__line {
    position: absolute;
    top: $margin-top-bullet + $size-bullet / 2;
    bottom: - ($margin-bottom-date + $margin-top-bullet + $size-bullet / 2);
    left: $spacing-8;
    z-index: 1;

    display: block;
    width: 4px;

    background-color: $blue;

    transform: translateX(-50%);

    @include media(min-width, m) {
      position: absolute;
      left: 0;

      width: 100%;
      height: 4px;

      transform: translateY(-50%);
    }
  }

  &__label {
    @include font-style(h4, $color: $blue);

    z-index: 2;

    order: 2;
    margin: 0;

    @include media(min-width, m) {
      @include font-style(h3, $color: $blue);

      order: 1;
    }
  }

  &__text {
    order: 3;
    margin: 0;
    grid-column-start: 2;

    @include media(min-width, m) {
      max-width: 240px;
    }
  }

  &__finish {
    @include font-style(p);

    margin-top: - $spacing-16;
    margin-left: $size-bullet + $spacing-16;

    @include media(min-width, m) {
      align-self: flex-start;
      margin-top: font-size(h3) + $size-bullet;
      margin-left: 0;

      // Align block with arrow
      transform: translateY(-50%);
    }
  }

  @include media(min-width, m) {
    flex-grow: 1;
    flex-direction: row;
    padding-left: 0;
  }
}
</style>
